import React, { JSX, createContext, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { DeviceListItemExtended, InstallationPages } from "../types/types";
import { Requester } from "../utils/Requester";
import { useSettings } from "./SettingsContextProvider";

interface Props {
  children: JSX.Element;
}

const OLD_TIME = new Date(2000, 1, 1);
const MILLISECONDS_IN_HOURS = 3_600_000;

export interface DeviceListViewContextInterface {
  installationList: Array<DeviceListItemExtended>,
  listMode: InstallationPages | undefined,
  setInstallationList: (updatedList: Array<DeviceListItemExtended>) => void,
  isLoading: boolean,
  isError: boolean,
  setListMode: (updatedListMode: InstallationPages | undefined) => void,
  refreshList: (mode: InstallationPages) => void,
  setCurrentMode: (mode: InstallationPages | undefined) => void,
  setIsLoading: (spin: boolean) => void,
}

export const DeviceListViewContext = createContext<DeviceListViewContextInterface>(
  // A proxy object allows to completely define the behavior of a proxied object
  // In this case if a parent component wont be wrapped in DeviceListViewContextProvider
  // Then any calls made to access the context values will result in
  // Being cached by the proxy object and display the errors bellow
  new Proxy({} as DeviceListViewContextInterface, {
    apply: () => {
      throw new Error("You must wrap your component in an DeviceListViewContextProvider");
    },
    get: () => {
      throw new Error("You must wrap your component in an DeviceListViewContextProvider");
    },
  }),
);

function DeviceListViewContextProvider({ children }: Props): React.ReactElement {
  const { t } = useTranslation();
  const { dashboardSettings } = useSettings();

  const [installationList, setInstallationList] = useState<Array<DeviceListItemExtended>>([]);
  const [listMode, setListMode] = useState<InstallationPages>(); // active or archived

  const [isLoading, setIsLoading] = useState(true); // its true so that on first load its already on
  const [isError, setIsError] = useState(false)

  // ----------------- PUBLIC INTERFACE ----------------- //
  const refreshList = useCallback((mode: InstallationPages) => {
    // Set which list we are going to save
    setListMode(mode);

    const passiveThreshold = new Date().getTime()
      - dashboardSettings.installations * MILLISECONDS_IN_HOURS;
    void Requester.getInstallList(mode)
      .then((res) => {
        let result: Array<DeviceListItemExtended> = [];
        if (mode === InstallationPages.Active) {
          result = Object.values(res.devices)
            .map((e) => {
              // Map latest installation to each device
              const item: DeviceListItemExtended = {
                device: e,
                isActive: false,
                hasNodes: false,
                staticDisplayName: t("Archived"),
                installationId: -1,
              };
              if (e.latest_installation !== null) {
                item.installation = res.installations[e.latest_installation];
                if (item.installation !== undefined) {
                  // ^ Is the latest installation available to the user?
                  item.installationId = item.installation.pk;
                  if (item.installation && item.installation.archived_time) {
                    // latest is archived branch
                    item.staticArchived = new Date(item.installation.archived_time);
                  } else {
                    // latest is active branch
                    item.staticDisplayName = item.installation.name;
                    item.hasNodes = item.installation.nodes > 0;
                  }
                }
              }
              if (e.last_online) {
                item.staticLastOnline = new Date(e.last_online);
              }
              return item;
            })
            .sort(
              (a, b) =>
                new Date(a.device.last_online || OLD_TIME).getTime()
                - new Date(b.device.last_online || OLD_TIME).getTime(),
            )
            .reverse();
        } else {
          result = Object.values(res.installations)
            .map((e) => {
              // Map latest installation to each device
              const item: DeviceListItemExtended = {
                installation: e,
                device: res.devices[e.device_fk],
                isActive: false,
                hasNodes: e.nodes > 0,
                staticDisplayName: e.name,
                installationId: e.pk,
              };

              if (e.last_online) {
                item.staticLastOnline = new Date(e.last_online);
              }
              if (e.archived_time) {
                item.staticArchived = new Date(e.archived_time);
              }
              return item;
            })
            .sort(
              (a, b) =>
                new Date(
                  a.installation?.archived_time || OLD_TIME,
                ).getTime()
                - new Date(b.installation?.archived_time || OLD_TIME).getTime(),
            )
            .reverse();
        }
        result = result.map((displayItem) => {
          if (
            displayItem.staticArchived !== undefined
            && displayItem.staticArchived.getTime() > passiveThreshold
          ) {
            // Latest installation is archived and has been archived within the specified Settings threshold.
            // we effectively ignore the `last_online` field for the `dashboardSettings.installations`
            // time period when the device has been archived
            displayItem.isActive = false;
          } else if (
            displayItem.staticLastOnline !== undefined
            && displayItem.staticLastOnline.getTime() > passiveThreshold
          ) {
            // Device has been online within the specified threshold
            displayItem.isActive = true;
          } else {
            // passive in all other cases
            displayItem.isActive = false;
          }

          // Shows the latest GSM signal strength even if the installation has gone PASSIVE.
          displayItem.connectivity = displayItem.installation?.connectivity;
          return displayItem;
        });
        setInstallationList(result);
      })
      .then(() => setIsLoading(false))
      .catch(() => setIsError(true));
  }, [dashboardSettings.installations, t]);

  const setCurrentMode = useCallback((mode: InstallationPages | undefined) => {
    if (mode !== listMode && mode !== undefined) {
      setIsLoading(true);
      refreshList(mode);
    } else {
      setIsLoading(false);
    }
  }, [listMode, refreshList]);

  return (
    <DeviceListViewContext.Provider
      value={{
        installationList,
        listMode,
        setInstallationList,
        isLoading,
        isError,
        setListMode,
        refreshList,
        setCurrentMode,
        setIsLoading,
      }}
    >
      {children}
      {/* Report generator probably goes here */}
    </DeviceListViewContext.Provider>
  );
}

const useDeviceListViewContext = (): DeviceListViewContextInterface => React.useContext(DeviceListViewContext);

export { DeviceListViewContextProvider, useDeviceListViewContext };
